import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Conversation {
  id: number;
  guest_name: string;
  property_name: string;
  platform: string;
  check_in?: string;
  check_out?: string;
  checkout_at?: string;
  column: string;
  guest_rating?: string;
  claim_id?: number;
  claim_amount?: number;
  claim_status?: string;
  claim_reason?: string;
  review_text?: string;
}

export interface DashboardData {
  arriving_soon: Conversation[];
  current_guest: Conversation[];
  review_period: Conversation[];
  claim_period: Conversation[];
}

interface Property {
  id: number;
  name: string;
}

export interface PastGuest {
  booking_id: number;
  guest_name: string;
  property_id: number;
  property_name: string;
  currency: string;
  checkin_date: string;
  checkout_date: string;
  total_spent: number;
  platform: string;
  conversation_id: number | null;
}

const STALE_TIME_MS = 60_000;

export const guestKeys = {
  dashboard: ['guests', 'dashboard'] as const,
  pastGuests: (propertyId: string | number) => ['guests', 'past', String(propertyId)] as const,
  properties: ['properties'] as const,
};

export function useDashboardQuery() {
  return useQuery<DashboardData>({
    queryKey: guestKeys.dashboard,
    queryFn: api.getDashboard,
    staleTime: STALE_TIME_MS,
  });
}

export function usePastGuestsQuery(propertyId: string | number = 'all') {
  return useQuery<PastGuest[]>({
    queryKey: guestKeys.pastGuests(propertyId),
    queryFn: () => api.getPastGuests({ property_id: propertyId }),
    staleTime: STALE_TIME_MS,
  });
}

export function usePropertiesQuery() {
  return useQuery<Property[]>({
    queryKey: guestKeys.properties,
    queryFn: api.getProperties,
    staleTime: STALE_TIME_MS,
  });
}

export function useRefreshGuests() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: guestKeys.dashboard });
    queryClient.invalidateQueries({ queryKey: ['guests', 'past'] });
  };
}

export function useUpdateColumnMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, manual_column }: { id: number; manual_column: string | null }) =>
      api.updateColumn(id, manual_column),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guestKeys.dashboard });
    },
  });
}
